import { toast as sonnerToast } from "sonner";
import {
  CircleCheck,
  CircleAlert,
  TriangleAlert,
  Info,
  Loader2,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

/** Default toast duration in milliseconds */
const DEFAULT_DURATION = 15000;

/** Toast type constants for type-safe usage */
export const ToastType = {
  success: "success",
  error: "error",
  warning: "warning",
  info: "info",
  loading: "loading",
} as const;

/** Toast type determines the icon and styling */
export type ToastType = (typeof ToastType)[keyof typeof ToastType];

/**
 * Action button configuration for toast
 */
export interface ToastAction {
  /** Button label text */
  label: string;
  /** Callback when button is clicked */
  onClick: () => void;
}

/**
 * Options for the fluid toast
 */
export interface FluidToastOptions {
  /** The main title of the toast */
  title: string;
  /** Optional description displayed below the title */
  description?: string;
  /** Type of toast - determines the icon and styling (default: "info") */
  type?: ToastType;
  /** Duration in milliseconds (default: 15000) */
  duration?: number;
  /** Error object to parse for details and request ID metadata */
  error?: unknown;
  /** Fallback description if error cannot be parsed */
  fallback?: string;
  /** Toast ID for updating existing toasts */
  id?: string | number;
  /** Optional action button */
  action?: ToastAction;
}

/** Config for each toast type */
const TOAST_CONFIG: Record<
  ToastType,
  {
    borderClasses: string;
    iconClasses: string;
    Icon: LucideIcon;
    spin: boolean;
  }
> = {
  success: {
    borderClasses: "border-l-green-500",
    iconClasses: "text-green-600",
    Icon: CircleCheck,
    spin: false,
  },
  error: {
    borderClasses: "border-l-red-500",
    iconClasses: "text-red-600",
    Icon: CircleAlert,
    spin: false,
  },
  warning: {
    borderClasses: "border-l-yellow-500",
    iconClasses: "text-yellow-600",
    Icon: TriangleAlert,
    spin: false,
  },
  info: {
    borderClasses: "border-l-transparent",
    iconClasses: "text-gray-500",
    Icon: Info,
    spin: false,
  },
  loading: {
    borderClasses: "border-l-transparent",
    iconClasses: "text-gray-500",
    Icon: Loader2,
    spin: true,
  },
};

/**
 * Converts snake_case or camelCase field names to Title Case.
 */
function formatFieldName(field: string): string {
  return field
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ")
    .trim();
}

interface ParsedFieldError {
  field: string;
  messages: string[];
}

const requestIdLinePrefix = "Request ID: ";

function getStringRequestId(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function getRequestIdFromJsonBody(body: unknown): string | undefined {
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    return undefined;
  }

  const record = body as Record<string, unknown>;
  const meta = record.meta;

  return (
    getStringRequestId(record.request_id) ??
    getStringRequestId(record.requestId) ??
    (meta && typeof meta === "object" && !Array.isArray(meta)
      ? (getStringRequestId((meta as Record<string, unknown>).request_id) ??
        getStringRequestId((meta as Record<string, unknown>).requestId))
      : undefined)
  );
}

function isRequestIdMetadataKey(key: string, value: unknown): boolean {
  if (key === "request_id" || key === "requestId") {
    return getStringRequestId(value) !== undefined;
  }

  if (key !== "meta") {
    return false;
  }

  return getRequestIdFromJsonBody(value) !== undefined;
}

function getStringEnvelopeMessage(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function isScalarEnvelopeMessageKey(key: string, value: unknown): boolean {
  return (
    (key === "message" || key === "error_message" || key === "error") &&
    getStringEnvelopeMessage(value) !== undefined
  );
}

function formatArrayFieldErrorMessage(item: unknown): string {
  if (typeof item === "string") {
    return item;
  }

  try {
    const json = JSON.stringify(item);
    if (typeof json === "string") {
      return json;
    }
  } catch {
    // Fall through to String(item) for values JSON cannot serialize, such as
    // BigInt or circular objects.
  }

  return String(item);
}

function hasOwnKey(record: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function getTopLevelScalarEnvelopeMessage(
  record: Record<string, unknown>,
): string | undefined {
  return (
    getStringEnvelopeMessage(record.message) ??
    getStringEnvelopeMessage(record.error_message) ??
    getStringEnvelopeMessage(record.error)
  );
}

function matchesParentApiErrorMessage(
  message: string,
  parentApiErrorMessage: string | undefined,
): boolean {
  return getStringEnvelopeMessage(parentApiErrorMessage) === message;
}

function hasFieldErrorEntries(record: Record<string, unknown>): boolean {
  for (const [key, value] of Object.entries(record)) {
    if (
      isRequestIdMetadataKey(key, value) ||
      isScalarEnvelopeMessageKey(key, value)
    ) {
      continue;
    }

    if (Array.isArray(value)) {
      if (value.length > 0) {
        return true;
      }
      continue;
    }

    if (typeof value === "string" && value.length > 0) {
      return true;
    }

    if (value && typeof value === "object") {
      if (hasFieldErrorEntries(value as Record<string, unknown>)) {
        return true;
      }
    }
  }

  return false;
}

function hasEnvelopeMetadata(record: Record<string, unknown>): boolean {
  return (
    getRequestIdFromJsonBody(record) !== undefined ||
    hasOwnKey(record, "errors") ||
    hasOwnKey(record, "details") ||
    hasOwnKey(record, "detail") ||
    hasOwnKey(record, "status") ||
    hasOwnKey(record, "code")
  );
}

function isTopLevelScalarEnvelope(
  record: Record<string, unknown>,
  parentApiErrorMessage?: string,
): boolean {
  const message = getTopLevelScalarEnvelopeMessage(record);
  return (
    message !== undefined &&
    (hasEnvelopeMetadata(record) ||
      hasFieldErrorEntries(record) ||
      matchesParentApiErrorMessage(message, parentApiErrorMessage))
  );
}

function getNestedErrorEnvelopeMessage(value: unknown): string | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  return (
    getStringEnvelopeMessage(record.message) ??
    getStringEnvelopeMessage(record.error_message)
  );
}

function getEnvelopeMessageFromJsonBody(
  body: unknown,
  parentApiErrorMessage?: string,
): string | undefined {
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    return undefined;
  }

  const record = body as Record<string, unknown>;
  if (isTopLevelScalarEnvelope(record, parentApiErrorMessage)) {
    return getTopLevelScalarEnvelopeMessage(record);
  }

  return getNestedErrorEnvelopeMessage(record.error);
}

function hasNestedErrorEnvelopeMessage(value: unknown): boolean {
  return getNestedErrorEnvelopeMessage(value) !== undefined;
}

function getApiRequestId(error: unknown): string | undefined {
  if (!error || typeof error !== "object") {
    return undefined;
  }

  const record = error as Record<string, unknown>;
  return (
    getStringRequestId(record.requestId) ??
    getStringRequestId(record.request_id) ??
    getRequestIdFromJsonBody(record.data)
  );
}

function appendRequestId(
  description: string | undefined,
  requestId: string | undefined,
): string | undefined {
  if (!requestId) {
    return description;
  }

  const requestIdLine = `${requestIdLinePrefix}${requestId}`;
  if (!description || description.trim().length === 0) {
    return requestIdLine;
  }

  if (description.split("\n").includes(requestIdLine)) {
    return description;
  }

  return `${description}\n${requestIdLine}`;
}

function removeDuplicateTitleFromDescription(
  title: string,
  description: string | undefined,
): string | undefined {
  if (!description) {
    return undefined;
  }

  const [firstLine, ...remainingLines] = description.split("\n");
  const trimmedFirstLine = firstLine?.trim();
  const trimmedTitle = title.trim();
  const titlePrefix = trimmedFirstLine
    ? trimmedTitle.slice(0, -trimmedFirstLine.length).trimEnd()
    : "";
  const titleEndsWithDuplicateMessage =
    trimmedFirstLine !== undefined &&
    trimmedFirstLine.length > 0 &&
    trimmedTitle.endsWith(trimmedFirstLine) &&
    (titlePrefix.endsWith(":") || titlePrefix.endsWith("："));

  if (
    !trimmedFirstLine ||
    (trimmedFirstLine !== trimmedTitle && !titleEndsWithDuplicateMessage)
  ) {
    return description;
  }

  const remainingDescription = remainingLines.join("\n");
  return remainingDescription.trim().length > 0
    ? remainingDescription
    : undefined;
}

/**
 * Type guard to check if an error looks like an API error.
 */
function isApiLikeError(error: unknown): error is {
  message: string | undefined;
  status: number;
  data: unknown;
  requestId?: unknown;
  request_id?: unknown;
} {
  if (!error || typeof error !== "object") {
    return false;
  }

  const err = error as Record<string, unknown>;
  return (
    typeof err.status === "number" &&
    "data" in err &&
    (typeof err.message === "string" || err.message === undefined)
  );
}

/**
 * Extracts field-level errors from API error data.
 */
function extractFieldErrorsFromData(
  data: unknown,
  { skipScalarEnvelopeKeys }: { skipScalarEnvelopeKeys: boolean },
): ParsedFieldError[] {
  const errors: ParsedFieldError[] = [];

  if (!data || typeof data !== "object") {
    return errors;
  }

  const errorObj = data as Record<string, unknown>;

  for (const [key, value] of Object.entries(errorObj)) {
    if (
      isRequestIdMetadataKey(key, value) ||
      (skipScalarEnvelopeKeys && isScalarEnvelopeMessageKey(key, value))
    ) {
      continue;
    }

    if (Array.isArray(value) && value.length > 0) {
      errors.push({
        field: formatFieldName(key),
        messages: value.map(formatArrayFieldErrorMessage),
      });
    } else if (typeof value === "string" && value.length > 0) {
      errors.push({
        field: formatFieldName(key),
        messages: [value],
      });
    } else if (value && typeof value === "object" && !Array.isArray(value)) {
      const nestedErrors = extractFieldErrorsFromData(value, {
        skipScalarEnvelopeKeys:
          key === "error" && hasNestedErrorEnvelopeMessage(value),
      });
      nestedErrors.forEach((nestedError) => {
        errors.push({
          field: `${formatFieldName(key)} → ${nestedError.field}`,
          messages: nestedError.messages,
        });
      });
    }
  }

  return errors;
}

/**
 * Formats field errors into a readable description string.
 */
function formatErrorDescription(errors: ParsedFieldError[]): string {
  if (errors.length === 0) {
    return "";
  }

  if (errors.length === 1) {
    const err = errors[0];
    if (!err) return "";
    const message = err.messages[0] || "is invalid";
    return `${err.field} ${message}`;
  }

  if (errors.length <= 3) {
    return errors
      .map((error) => `${error.field} ${error.messages[0] || "is invalid"}`)
      .join("\n");
  }

  const shown = errors
    .slice(0, 3)
    .map((error) => `${error.field} ${error.messages[0] || "is invalid"}`)
    .join("\n");
  const remaining = errors.length - 3;
  return `${shown}\n...and ${remaining} more ${remaining === 1 ? "error" : "errors"}`;
}

function getErrorDataDescription(
  data: unknown,
  parentApiErrorMessage?: string,
): string | undefined {
  const topLevelScalarEnvelopeMessage =
    data && typeof data === "object" && !Array.isArray(data)
      ? isTopLevelScalarEnvelope(
          data as Record<string, unknown>,
          parentApiErrorMessage,
        )
        ? getTopLevelScalarEnvelopeMessage(data as Record<string, unknown>)
        : undefined
      : undefined;
  const fieldErrors = extractFieldErrorsFromData(data, {
    skipScalarEnvelopeKeys: topLevelScalarEnvelopeMessage !== undefined,
  });
  const fieldDescription =
    fieldErrors.length > 0 ? formatErrorDescription(fieldErrors) : undefined;
  const envelopeMessage = getEnvelopeMessageFromJsonBody(
    data,
    parentApiErrorMessage,
  );

  if (topLevelScalarEnvelopeMessage && fieldDescription) {
    return fieldDescription;
  }

  if (
    envelopeMessage &&
    fieldDescription &&
    fieldDescription !== envelopeMessage
  ) {
    return `${envelopeMessage}\n${fieldDescription}`;
  }

  return fieldDescription ?? envelopeMessage;
}

/**
 * Extracts description from an error object.
 */
function getErrorDescription(
  error: unknown,
  fallback?: string,
): string | undefined {
  const requestId = getApiRequestId(error);
  let description: string | undefined;

  if (isApiLikeError(error)) {
    if (error.data) {
      description = getErrorDataDescription(error.data, error.message);
    }

    if (!description && error.message) {
      description = error.message;
    }
  } else if (error instanceof Error) {
    description = error.message;
  }

  return appendRequestId(description ?? fallback, requestId);
}

// ---------------------------------------------------------------------------
// Toast UI
// ---------------------------------------------------------------------------

function ToastContent({
  title,
  description,
  type,
  toastId,
  action,
}: {
  title: string;
  description?: string | undefined;
  type: ToastType;
  toastId?: string | number | undefined;
  action?: ToastAction | undefined;
}): React.JSX.Element {
  const config = TOAST_CONFIG[type];
  const { Icon } = config;

  return (
    <div
      className={`flex w-[min(450px,calc(100vw-2rem))] items-start gap-2 rounded-lg border border-l-[5px] bg-white px-3 py-4 shadow-md ${config.borderClasses}`}
    >
      <div className={`mt-px shrink-0 ${config.iconClasses}`}>
        <Icon className={`h-5 w-5${config.spin ? " animate-spin" : ""}`} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-base leading-6 font-semibold text-gray-900">
          {title}
        </div>
        {description && (
          <div className="mt-1 text-sm leading-5 whitespace-pre-wrap text-gray-600">
            {description}
          </div>
        )}
        {action && (
          <button
            onClick={() => {
              action.onClick();
              sonnerToast.dismiss(toastId);
            }}
            className="mt-2 text-sm font-medium text-blue-600 transition-colors hover:text-blue-800"
          >
            {action.label}
          </button>
        )}
      </div>
      <button
        aria-label="Dismiss toast"
        onClick={() => sonnerToast.dismiss(toastId)}
        className="mt-px shrink-0 text-gray-400 transition-colors hover:text-gray-600"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

function showToast(options: FluidToastOptions): string | number {
  const {
    title,
    description,
    type = "info",
    duration = DEFAULT_DURATION,
    error,
    fallback,
    id,
    action,
  } = options;

  let finalDescription = description;

  // Automatically parse error objects for useful details and request IDs.
  if (description !== undefined) {
    finalDescription = appendRequestId(description, getApiRequestId(error));
  } else if (error) {
    finalDescription = removeDuplicateTitleFromDescription(
      title,
      getErrorDescription(error, fallback),
    );
  } else if (fallback && fallback.trim() !== title.trim()) {
    finalDescription = fallback;
  }

  const toastId = id ?? crypto.randomUUID();

  return sonnerToast.custom(
    () => (
      <ToastContent
        title={title}
        description={finalDescription}
        type={type}
        toastId={toastId}
        action={action}
      />
    ),
    { duration, id: toastId },
  );
}

function showLoading(
  title: string,
  options?: Omit<FluidToastOptions, "title" | "type">,
): string | number {
  return showToast({ title, type: "loading", duration: Infinity, ...options });
}

function dismissToast(toastId?: string | number): void {
  sonnerToast.dismiss(toastId);
}

/**
 * Rich toast utility with custom UI and typed icons.
 *
 * @example
 * fluidToast({ title: "Saved", type: "success" });
 * fluidToast({ title: "Failed", type: "error", error: apiError });
 * const id = fluidToast.loading("Processing...");
 * fluidToast({ title: "Done!", type: "success", id });
 */
export const fluidToast: typeof showToast & {
  loading: typeof showLoading;
  dismiss: typeof dismissToast;
} = Object.assign(showToast, {
  loading: showLoading,
  dismiss: dismissToast,
});
